import { type MedplumClient, type PatchOperation, type QueryTypes, resolveId } from "@medplum/core";
import type {
  Extension,
  HealthcareService,
  Money,
  Questionnaire,
  Reference,
} from "@medplum/fhirtypes";
import { findIdentifier } from "@vibrant/models/fhir/fhir-resource-utils";
import type Stripe from "stripe";
import {
  STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS,
  STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINK_URLS,
  STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINKS,
  STRIPE_IDENTIFIER_SYSTEM_PRICES,
  STRIPE_IDENTIFIER_SYSTEM_PRODUCTS,
} from "@/lib/stripe/constants";
import {
  type EmailTemplate,
  getEmailTemplate,
  type SerializedEmailTemplate,
  VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES,
} from "@/models/communications/EmailTemplate";
import { Practice } from "@/models/practice/Practice";
import { Survey } from "@/models/questionnaires/Survey";
import {
  type ExtensionPatchRequest,
  findExtension,
  getExtensionPatchOperations,
  getIdentifierPatchOperations,
  type IdentifierPatchRequest,
} from "@/models/utils";

const VIBRANT_EXTENSION_URL_SERVICE_PAYMENT_SCHEDULE = "vibrant.services.paymentSchedule";
const VIBRANT_EXTENSION_URL_SERVICE_PRICE = "vibrant.services.price";
const VIBRANT_EXTENSION_URL_SERVICE_SURVEYS = "vibrant.services.surveys";
// Shared coding system for Questionnaire references embedded in a CodeableConcept extension —
// same system/code shape as PracticeAppointmentType.Extension.Surveys and ProviderSchedule.
const SURVEY_REFERENCE_CODING_SYSTEM = "vibrant.survey-reference";

export type SerializedService = HealthcareService;

export type PaymentSchedule =
  | "one-time"
  | "monthly subscription"
  | "quarterly subscription"
  | "six-month subscription"
  | "yearly subscription";

export interface CreateServiceRequest {
  practiceId: string;
  serviceName: string;
  paymentSchedule: PaymentSchedule;
  priceCents: number;
  stripeProduct: Stripe.Product;
  stripePrice: Stripe.Price;
  stripeAccountId?: string;
  postPurchaseEmail?: SerializedEmailTemplate;
}

export interface ServiceSearchQuery {
  stripePaymentLinkId?: string;
}

export interface ServicePatchQuery {
  stripeProductId?: string | null;
  stripePriceId?: string | null;
  stripePaymentLinkId?: string | null;
  stripePaymentLinkUrl?: string | null;
  stripeAccountId?: string | null;
  postPurchaseEmail?: SerializedEmailTemplate;
}

// TODO inherit from FhirResource
export class Service {
  constructor(readonly healthcareService: HealthcareService) {}

  get id(): string {
    return this.healthcareService.id!;
  }

  get name(): string {
    const name = this.healthcareService.name;

    if (!name) {
      throw new Error("Service name not found");
    }
    return name;
  }

  /**
   * Get the practice that the service is provided by
   *
   * @param medplum - The medplum client
   * @returns The practice that the service is provided by
   */
  async getPractice(medplum: MedplumClient): Promise<Practice> {
    const organizationReference = this.healthcareService.providedBy;

    if (!organizationReference) {
      throw new Error("Service has no practice");
    }
    const organizationId = resolveId(organizationReference);
    if (!organizationId) {
      throw new Error("organizationId not found");
    }
    const organization = await medplum.readResource("Organization", organizationId);
    return new Practice(organization);
  }

  get paymentSchedule(): PaymentSchedule {
    const paymentSchedule = findExtension(
      this.healthcareService,
      VIBRANT_EXTENSION_URL_SERVICE_PAYMENT_SCHEDULE,
    )?.valueCode;

    if (!paymentSchedule) {
      throw new Error("Payment schedule not found");
    }
    return paymentSchedule as PaymentSchedule;
  }

  get price(): Money {
    const price = findExtension(
      this.healthcareService,
      VIBRANT_EXTENSION_URL_SERVICE_PRICE,
    )?.valueMoney;

    if (!price) {
      throw new Error("Price not found");
    }
    return price;
  }

  /**
   * Questionnaire references attached to this service, in the same CodeableConcept-coding-array
   * shape used by PracticeAppointmentType.Extension.Surveys.
   */
  get surveyReferences(): Reference<Questionnaire>[] {
    const codings =
      findExtension(this.healthcareService, VIBRANT_EXTENSION_URL_SERVICE_SURVEYS)
        ?.valueCodeableConcept?.coding ?? [];

    return codings
      .filter(
        (coding) =>
          coding.system === SURVEY_REFERENCE_CODING_SYSTEM &&
          coding.code?.startsWith("Questionnaire/"),
      )
      .map((coding) => ({ reference: coding.code }) as Reference<Questionnaire>);
  }

  /**
   * Resolves the Surveys attached to this service, excluding any that aren't published — a
   * draft Questionnaire must never become a required onboarding step just because an admin
   * attached it to a Service before publishing it.
   */
  async getSurveys(medplum: MedplumClient): Promise<Survey[]> {
    const refs = this.surveyReferences;
    if (refs.length === 0) return [];

    const surveys = await Promise.all(
      refs.map(async (ref) => {
        try {
          return await Survey.getByReference(medplum, ref);
        } catch {
          return undefined;
        }
      }),
    );

    return surveys.filter((survey): survey is Survey => survey?.isPublished === true);
  }

  /**
   * Sets the Surveys attached to this service, replacing any existing selection.
   */
  async setSurveys(medplum: MedplumClient, surveys: (Survey | string)[]): Promise<Service> {
    const uniqueSurveyIds = [
      ...new Set(
        surveys.map((survey) => {
          const surveyId = typeof survey === "string" ? survey : survey.id;
          if (!surveyId) {
            throw new Error("All surveys must have a persistent id");
          }
          return surveyId;
        }),
      ),
    ];

    // getExtensionPatchOperations' generic value-type dispatch doesn't produce
    // `valueCodeableConcept` for object values, so this extension is patched as a whole-array
    // replace instead of going through that helper.
    const existingExtensions = this.healthcareService.extension ?? [];
    const otherExtensions = existingExtensions.filter(
      (ext) => ext.url !== VIBRANT_EXTENSION_URL_SERVICE_SURVEYS,
    );
    const surveysExtension: Extension[] =
      uniqueSurveyIds.length > 0
        ? [
            {
              url: VIBRANT_EXTENSION_URL_SERVICE_SURVEYS,
              valueCodeableConcept: {
                coding: uniqueSurveyIds.map((surveyId) => ({
                  system: SURVEY_REFERENCE_CODING_SYSTEM,
                  code: `Questionnaire/${surveyId}`,
                })),
              },
            },
          ]
        : [];
    const updatedExtensions = [...otherExtensions, ...surveysExtension];

    const healthcareService = await medplum.patchResource("HealthcareService", this.id, [
      {
        op: existingExtensions.length > 0 ? "replace" : "add",
        path: "/extension",
        value: updatedExtensions,
      },
    ]);
    return new Service(healthcareService);
  }

  get purchaseEmailTemplateId(): string | undefined {
    return findExtension(this.healthcareService, VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES)
      ?.valueString;
  }

  async purchaseEmailTemplate(medplum: MedplumClient): Promise<EmailTemplate | null> {
    const purchaseEmailId = this.purchaseEmailTemplateId;
    if (!purchaseEmailId) {
      return null;
    }
    return getEmailTemplate(medplum, purchaseEmailId);
  }

  /**
   * The Stripe objects backing this service. Members are null on services that predate the
   * Stripe-backed Services flow or were migrated without identifiers. Lifecycle work (archive,
   * delete) reads this rather than the throwing getters below so a legacy service can still be
   * taken out of service instead of failing on a missing id.
   */
  get stripeObjects(): {
    productId: string | null;
    priceId: string | null;
    paymentLinkId: string | null;
  } {
    return {
      productId:
        findIdentifier(this.healthcareService, STRIPE_IDENTIFIER_SYSTEM_PRODUCTS)?.value || null,
      priceId:
        findIdentifier(this.healthcareService, STRIPE_IDENTIFIER_SYSTEM_PRICES)?.value || null,
      paymentLinkId: this.stripePaymentLinkId,
    };
  }

  get stripeProductId(): string {
    const { productId } = this.stripeObjects;
    if (!productId) {
      throw new Error("Service has no Stripe product ID");
    }
    return productId;
  }

  get stripePriceId(): string {
    const { priceId } = this.stripeObjects;
    if (!priceId) {
      throw new Error("Service has no Stripe price ID");
    }
    return priceId;
  }

  get stripePaymentLinkId(): string | null {
    return (
      findIdentifier(this.healthcareService, STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINKS)?.value || null
    );
  }

  get stripeAccountId(): string | null {
    return findIdentifier(this.healthcareService, STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS)?.value || null;
  }

  async deleteStripePaymentLink(medplum: MedplumClient): Promise<Service> {
    const identifierPatchRequests: IdentifierPatchRequest[] = [
      {
        resource: this.healthcareService,
        system: STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINKS,
        value: null,
      },
    ];
    const extensionPatchRequests: ExtensionPatchRequest[] = [
      {
        resource: this.healthcareService,
        url: STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINK_URLS,
        value: null,
      },
    ];

    const healthcareService = await medplum.patchResource("HealthcareService", this.id, [
      ...getIdentifierPatchOperations(this.healthcareService, identifierPatchRequests),
      ...getExtensionPatchOperations(this.healthcareService, extensionPatchRequests),
    ]);
    return new Service(healthcareService);
  }

  get stripePaymentLinkUrl(): string | null {
    return (
      findExtension(this.healthcareService, STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINK_URLS)
        ?.valueString || null
    );
  }

  serialize(): SerializedService {
    return this.healthcareService;
  }

  async archive(medplum: MedplumClient): Promise<Service> {
    const healthcareService = await medplum.updateResource<HealthcareService>({
      ...this.healthcareService,
      active: false,
    });
    return new Service(healthcareService);
  }

  async delete(medplum: MedplumClient): Promise<Service> {
    await medplum.deleteResource("HealthcareService", this.id);
    return this;
  }

  static async searchByIds(medplum: MedplumClient, serviceIds: string[]): Promise<Service[]> {
    const healthcareServices = await medplum.searchResources("HealthcareService", {
      _id: serviceIds.join(","),
    });
    return healthcareServices.map((service) => new Service(service));
  }
}

export async function createService(
  medplum: MedplumClient,
  request: CreateServiceRequest,
): Promise<Service> {
  const healthcareService = await medplum.createResource<HealthcareService>({
    resourceType: "HealthcareService",
    providedBy: { reference: `Organization/${request.practiceId}` },
    name: request.serviceName,
    active: true,
    identifier: [
      { system: STRIPE_IDENTIFIER_SYSTEM_PRODUCTS, value: request.stripeProduct.id },
      { system: STRIPE_IDENTIFIER_SYSTEM_PRICES, value: request.stripePrice.id },
      request.stripeAccountId
        ? { system: STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS, value: request.stripeAccountId }
        : undefined,
    ].filter(Boolean) as HealthcareService["identifier"],
    extension: [
      { url: VIBRANT_EXTENSION_URL_SERVICE_PAYMENT_SCHEDULE, valueCode: request.paymentSchedule },
      {
        url: VIBRANT_EXTENSION_URL_SERVICE_PRICE,
        valueMoney: { value: request.priceCents / 100, currency: "USD" },
      },
      request.postPurchaseEmail
        ? {
            url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES,
            valueString: request.postPurchaseEmail.id,
          }
        : undefined,
    ].filter(Boolean) as Extension[],
  });
  return new Service(healthcareService);
}

export async function getService(medplum: MedplumClient, serviceId: string): Promise<Service> {
  try {
    const healthcareService = await medplum.readResource("HealthcareService", serviceId);
    return new Service(healthcareService);
  } catch (_error) {
    throw new Error("Service not found");
  }
}

export async function searchOneService(
  medplum: MedplumClient,
  searchQuery: ServiceSearchQuery,
): Promise<Service | undefined> {
  // if searchquery has no keys, return null
  if (Object.keys(searchQuery).length === 0) {
    return undefined;
  }

  const queryTypes: QueryTypes = {};

  if (searchQuery.stripePaymentLinkId) {
    queryTypes.identifier = `${STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINKS}|${searchQuery.stripePaymentLinkId}`;
  }
  const healthcareService = await medplum.searchOne("HealthcareService", queryTypes);
  return healthcareService ? new Service(healthcareService) : undefined;
}

export async function patchService(
  medplum: MedplumClient,
  serviceId: string,
  patchQuery: ServicePatchQuery,
): Promise<Service> {
  const healthcareService = await medplum.readResource("HealthcareService", serviceId);

  const identifierPatchRequests: IdentifierPatchRequest[] = [];
  const extensionPatchRequests: ExtensionPatchRequest[] = [];

  if (patchQuery.stripeProductId !== undefined) {
    identifierPatchRequests.push({
      resource: healthcareService,
      system: STRIPE_IDENTIFIER_SYSTEM_PRODUCTS,
      value: patchQuery.stripeProductId,
    });
  }

  if (patchQuery.stripePriceId !== undefined) {
    identifierPatchRequests.push({
      resource: healthcareService,
      system: STRIPE_IDENTIFIER_SYSTEM_PRICES,
      value: patchQuery.stripePriceId,
    });
  }

  if (patchQuery.stripePaymentLinkId !== undefined) {
    identifierPatchRequests.push({
      resource: healthcareService,
      system: STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINKS,
      value: patchQuery.stripePaymentLinkId,
    });
  }

  if (patchQuery.stripePaymentLinkUrl !== undefined) {
    extensionPatchRequests.push({
      resource: healthcareService,
      url: STRIPE_IDENTIFIER_SYSTEM_PAYMENT_LINK_URLS,
      value: patchQuery.stripePaymentLinkUrl,
    });
  }

  if (patchQuery.stripeAccountId !== undefined) {
    identifierPatchRequests.push({
      resource: healthcareService,
      system: STRIPE_IDENTIFIER_SYSTEM_ACCOUNTS,
      value: patchQuery.stripeAccountId,
    });
  }

  if (patchQuery.postPurchaseEmail) {
    extensionPatchRequests.push({
      resource: healthcareService,
      url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES,
      value: patchQuery.postPurchaseEmail.id!,
    });
  }

  const identifierPatchOperations = getIdentifierPatchOperations(
    healthcareService,
    identifierPatchRequests,
  );
  const extensionPatchOperations = getExtensionPatchOperations(
    healthcareService,
    extensionPatchRequests,
  );

  const patchedHealthcareService = await medplum.patchResource(
    "HealthcareService",
    serviceId,
    [...identifierPatchOperations, ...extensionPatchOperations].filter(Boolean) as PatchOperation[],
  );
  return new Service(patchedHealthcareService);
}
